import { useEffect } from 'react'

const LAST_RELOAD_KEY = 'jotta:errorPage:lastOnlineReload'
/** At most one automatic reload per this many milliseconds. */
export const RELOAD_COOLDOWN_MS = 30_000

function readLastReload(): number {
  try {
    return Number(window.sessionStorage.getItem(LAST_RELOAD_KEY)) || 0
  } catch {
    // Storage can be blocked (private mode, sandboxed frames)
    return 0
  }
}

function writeLastReload(now: number) {
  try {
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now))
  } catch {
    // Without storage we still reload, just without the cooldown
  }
}

/**
 * A network failure while the device was offline usually ends by itself:
 * reload when the browser says it is back online, or when the user returns
 * to the tab after we saw it go offline. Errors while online (a 503, a
 * timeout) don't reload, so a down backend doesn't loop on every focus.
 */
export function useReloadWhenBackOnline(networkError: boolean) {
  useEffect(() => {
    if (!networkError) {
      return
    }
    // Only reload if the device was really offline at some point
    let sawOffline = !navigator.onLine
    const reload = () => {
      const now = Date.now()
      if (now - readLastReload() < RELOAD_COOLDOWN_MS) {
        return
      }
      writeLastReload(now)
      window.location.reload()
    }
    const onOffline = () => {
      sawOffline = true
    }
    const onOnline = () => {
      sawOffline = true
      reload()
    }
    const onVisible = () => {
      if (
        sawOffline &&
        document.visibilityState === 'visible' &&
        navigator.onLine
      ) {
        reload()
      }
    }
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [networkError])
}
